/**
 * Expected AI Parser Response Structure (from prompt):
// {
//   "invoiceTotalAfterTax": 600,
//   "tax": 0,  // Single tax value (or use tax_line_items for multi-tax)
//   "tax_line_items": [  // Optional: For invoices with multiple taxes (GST, PST, etc.)
//     {
//       "tax_type": "GST",
//       "amount": 50
//     },
//     {
//       "tax_type": "PST",
//       "amount": 70
//     }
//   ],
//   "discount": 164.14,
//   "discount_percentage": 0.2148011588,
//   "invoiceTotalBeforeTax": 764.14,
//   "line_items": [
//     {
//       "description": "trip",
//       "quantity": 1,
//       "unit_price": 112.5,
//       "extended_price": 112.5,
//       "extended_price_after_discount": 88.34,
//       "discount_amount": 24.16,
//       "category": "travel"
//     },
//     {
//       "description": "Assessment",
//       "quantity": 1,
//       "unit_price": 127.5,
//       "extended_price": 127.5,
//       "extended_price_after_discount": 99.74,
//       "discount_amount": 27.76,
//       "category": "labor"
//     },
//     {
//       "description": "Labor",
//       "quantity": 2,
//       "unit_price": 127.5,
//       "extended_price": 255,
//       "extended_price_after_discount": 199.48,
//       "discount_amount": 55.52,
//       "category": "labor"
//     },
//     {
//       "description": "Snaking fees",
//       "quantity": 1,
//       "unit_price": 250,
//       "extended_price": 250,
//       "extended_price_after_discount": 195.4,
//       "discount_amount": 54.6,
//       "category": "other"
//     },
//     {
//       "description": "Drain Openers",
//       "quantity": 1,
//       "unit_price": 19.14,
//       "extended_price": 19.14,
//       "extended_price_after_discount": 14.9,
//       "discount_amount": 4.24,
//       "category": "other"
//     }
//   ],
//   "scope": null,
//   "invoice_number": "6681",
//   "invoice_date": "05/19/2025",
//   "vendor_name": "Foothills Maintenance Services LLC",
//   "customer_name": "All Systems Facility Services LLC"
// }
 */

import { nullSafeGetOrElse } from './DataAccessUtils';

// Constants
const CONSTANTS = {
	DEFAULT_PAYMENT_TERMS: 'Net 30',
	DEFAULT_STATUS: 'draft',
	DEFAULT_UNIT_TYPE: 'each',
	DEFAULT_NUM_TECH: 1,
	DEFAULT_INITIAL_TOTAL: '0.00',
	DAYS_TO_MILLISECONDS: 24 * 60 * 60 * 1000,
	SECONDS_PER_HOUR: 3600,
	DEFAULT_DUE_DAYS: 30,
	EQUIPMENT_KEYWORDS: ['equipment', 'machine', 'unit', 'system', 'hvac', 'motor', 'compressor'],
};

// Helper Functions using existing project utilities
const parseNumber = (value: any, defaultValue = 0) => parseFloat(value) || defaultValue;

const parseAmount = (value: any, defaultValue = 0): number =>
	Number(parseNumber(value, defaultValue).toFixed(2));

// Get final amount with discount fallback
const getFinalAmount = (item: any): number => {
	return item.extended_price_after_discount != null
		? parseNumber(item.extended_price_after_discount)
		: parseNumber(item.extended_price);
};

// Calculate total from line items
const calculateTotal = (lineItems: any[]): string =>
	lineItems.reduce((sum, item) => sum + parseFloat(item.amount), 0).toFixed(2);

// Check if description indicates equipment
const isEquipmentDescription = (description: string): boolean => {
	if (!description) return false;
	return CONSTANTS.EQUIPMENT_KEYWORDS.some((keyword) =>
		description.toLowerCase().includes(keyword)
	);
};

// Generate temporary ID for materials
let tempIdCounter = -1;
const getNextTempId = () => tempIdCounter--;

// Adapter function to convert AI parser output to formData structure
export const adaptParsedInvoiceToFormData = (parsedData: any, workOrder: any) => {
	if (!parsedData || !workOrder) return null;

	const totalTax = parseNumber(parsedData.tax) || parseNumber(parsedData.invoiceTax);

	const adaptedFormData: any = {
		id: null,
		status: CONSTANTS.DEFAULT_STATUS,
		scope: nullSafeGetOrElse('scope', parsedData, ''),
		scopeNonFormatted: nullSafeGetOrElse('scope', parsedData, ''),
		externalWorkOrderId: workOrder.id,
		buyerInvoiceTypeDetail: [],
		supplierInvoiceTypeDetail: [],
		buyerCustomFieldValues: [],
		supplierCustomFieldValues: [],
		paymentTerms: CONSTANTS.DEFAULT_PAYMENT_TERMS,
		dueDate: new Date(Date.now() + CONSTANTS.DEFAULT_DUE_DAYS * CONSTANTS.DAYS_TO_MILLISECONDS),
		dateOfInvoice: parsedData.invoice_date ? new Date(parsedData.invoice_date) : new Date(),
		invoiceNumber: nullSafeGetOrElse('invoice_number', parsedData, ''),
		invoiceNotes: [],
		invoiceTax: parseAmount(totalTax),
		invoiceTotalBeforeTax: parseAmount(parsedData.invoiceTotalBeforeTax),
		invoiceTotalAfterTax: parseAmount(parsedData.invoiceTotalAfterTax),
		laborTotalBeforeTax: CONSTANTS.DEFAULT_INITIAL_TOTAL,
		laborLineItems: [],
		partsTotalBeforeTax: CONSTANTS.DEFAULT_INITIAL_TOTAL,
		equipmentTotalBeforeTax: CONSTANTS.DEFAULT_INITIAL_TOTAL,
		materialTotalBeforeTax: CONSTANTS.DEFAULT_INITIAL_TOTAL,
		materialLineItems: [],
		travelTotalBeforeTax: CONSTANTS.DEFAULT_INITIAL_TOTAL,
		travelLineItems: [],
		freightTotalBeforeTax: CONSTANTS.DEFAULT_INITIAL_TOTAL,
		freightLineItems: [],
		miscTotalBeforeTax: CONSTANTS.DEFAULT_INITIAL_TOTAL,
		miscLineItems: [],
		workOrderId: workOrder.id,
		workOrder: workOrder,
		AIData: parsedData,
	};

	// Group items by category with proper error handling.
	// Parser category values (per prompt): "labor" | "part" | "equipment" | "travel" | "freight" | "other"
	const groupItemsByCategory = (items: any[]) => {
		const categories: Record<string, any[]> = {
			labor: [],
			part: [],
			equipment: [],
			travel: [],
			freight: [],
			misc: [], // Default for 'other' and unknown categories
		};

		items.forEach((item) => {
			if (!item.category) {
				console.warn('Item missing category, defaulting to misc:', item);
				categories.misc.push(item);
				return;
			}

			const category = item.category.toLowerCase();
			// Handle 'other' category mapping to 'misc'
			const normalizedCategory = category === 'other' ? 'misc' : category;

			if (categories.hasOwnProperty(normalizedCategory)) {
				categories[normalizedCategory].push(item);
			} else {
				console.warn(`Unknown category "${category}" for item, defaulting to misc:`, item);
				categories.misc.push(item);
			}
		});

		return categories;
	};

	// Create labor item
	const createLaborItem = (item: any) => {
		const originalAmount = parseNumber(item.extended_price);
		const finalAmount = getFinalAmount(item);
		const quantity = parseNumber(item.quantity, 1);
		const unitPrice = parseNumber(item.unit_price);
		// Use original amount for hours calculation to maintain accuracy
		const hours = unitPrice > 0 ? originalAmount / unitPrice : quantity;
		const seconds = Math.round(hours * CONSTANTS.SECONDS_PER_HOUR);

		return {
			roleType: nullSafeGetOrElse('description', item, 'Labor'),
			numberOfTech: CONSTANTS.DEFAULT_NUM_TECH,
			hourlyRate: parseAmount(item.unit_price).toFixed(2),
			seconds,
			amount: parseAmount(finalAmount).toFixed(2),
			...(item.discount_amount != null && {
				discount_amount: parseAmount(item.discount_amount),
			}),
		};
	};

	const createMaterialItem = (item: any, categoryHint?: 'part' | 'equipment') => {
		const description = nullSafeGetOrElse('description', item, 'Material');
		const isEquipment =
			categoryHint === 'equipment' || (categoryHint == null && isEquipmentDescription(description));
		const finalAmount = getFinalAmount(item);

		return {
			description,
			partNumber: '',
			partId: isEquipment ? null : getNextTempId(),
			equipmentId: isEquipment ? getNextTempId() : null,
			unitType: CONSTANTS.DEFAULT_UNIT_TYPE,
			unitPrice: parseAmount(item.unit_price).toFixed(2),
			quantity: parseAmount(item.quantity, 1).toFixed(2),
			amount: parseAmount(finalAmount).toFixed(2),
			...(item.discount_amount != null && {
				discount_amount: parseAmount(item.discount_amount),
			}),
		};
	};

	// Create simple line item for travel, freight, misc
	const createSimpleLineItem = (item: any) => ({
		description: nullSafeGetOrElse('description', item, ''),
		amount: parseAmount(getFinalAmount(item)).toFixed(2),
		...(item.discount_amount != null && {
			discount_amount: parseAmount(item.discount_amount),
		}),
	});

	// Process line items by category
	const lineItems = parsedData.line_items || [];
	const itemsByCategory = groupItemsByCategory(lineItems);

	// Process all categories
	adaptedFormData.laborLineItems = itemsByCategory.labor.map(createLaborItem);

	// Material is further split into parts & equipment; use explicit category when available
	const partMaterialItems = itemsByCategory.part.map((item: any) =>
		createMaterialItem(item, 'part')
	);
	const equipmentMaterialItems = itemsByCategory.equipment.map((item: any) =>
		createMaterialItem(item, 'equipment')
	);
	adaptedFormData.materialLineItems = [...partMaterialItems, ...equipmentMaterialItems];

	adaptedFormData.travelLineItems = itemsByCategory.travel.map(createSimpleLineItem);
	adaptedFormData.freightLineItems = itemsByCategory.freight.map(createSimpleLineItem);
	adaptedFormData.miscLineItems = itemsByCategory.misc.map(createSimpleLineItem);

	// Calculate all totals
	adaptedFormData.laborTotalBeforeTax = calculateTotal(adaptedFormData.laborLineItems);
	adaptedFormData.travelTotalBeforeTax = calculateTotal(adaptedFormData.travelLineItems);
	adaptedFormData.freightTotalBeforeTax = calculateTotal(adaptedFormData.freightLineItems);
	adaptedFormData.miscTotalBeforeTax = calculateTotal(adaptedFormData.miscLineItems);

	// Calculate material totals (separate parts and equipment) - optimize filtering
	const partsItems: any[] = [];
	const equipmentItems: any[] = [];
	adaptedFormData.materialLineItems.forEach((item: any) => {
		if (item.partId) partsItems.push(item);
		if (item.equipmentId) equipmentItems.push(item);
	});

	adaptedFormData.partsTotalBeforeTax = calculateTotal(partsItems);
	adaptedFormData.equipmentTotalBeforeTax = calculateTotal(equipmentItems);
	adaptedFormData.materialTotalBeforeTax = calculateTotal(adaptedFormData.materialLineItems);

	return adaptedFormData;
};
